import User from "@/pcm/store/models/User";
import {first} from "lodash";
import {useBus} from "@/pcm/plugins/helper";
import * as Sentry from '@sentry/vue';
import {DateTime, Settings} from "luxon";

export default class {
    private _redirectUrlKey: string = 'authentication_sso_redirect';

    private _user: User | null = null;

    get redirectUrlKey(): string {
        return this._redirectUrlKey;
    }

    setRedirectUrl(url: string): void {
        if (window.location.pathname.startsWith('/auth')) {
            return;
        }

        window.localStorage.setItem(this.redirectUrlKey, url);
    }

    getRedirectUrl(remove: boolean = false): string | null {
        const url = window.localStorage.getItem(this.redirectUrlKey);

        if (remove) {
            this.removeRedirectUrl();
        }

        return url;
    }

    removeRedirectUrl(): void {
        return window.localStorage.removeItem(this.redirectUrlKey);
    }

    isLoggedIn(forceRedirect: boolean = false) {
        return new Promise(resolve => {
            if (this._user !== null) {
                return resolve(true);
            }

            // We do not know if the user is logged in
            this.getLoggedInUser()
                .then((user: User | null) => {
                    if (user === null) {
                        if (forceRedirect) {
                            this.setRedirectUrl(
                                window.location.pathname + window.location.search
                            );

                            return window.location = (window.global.domain + '/auth/login' as unknown as Location);
                        }

                        return resolve(false);
                    }

                    if (user.email_verified_at === null) {
                        if (forceRedirect) {
                            this.setRedirectUrl(
                                window.location.pathname + window.location.search
                            );

                            if (window.location.pathname !== '/auth/verify') {
                                return window.location = ('/auth/verify' as unknown as Location);
                            }
                        }

                        return resolve(false);
                    }

                    return resolve(true);
                });
        });
    }

    getLoggedInUser(): Promise<User | null> {
        return new Promise((resolve, reject) => {
            User.api().get('users/@me')
                .then(result => {
                    this._user = first(result.entities?.users) as User;

                    useBus().emit('setGateUser', this._user);

                    Sentry.setUser(result.response.data.data);

                    if (this._user.preferred_locale !== null) {
                        Settings.defaultLocale = this._user.preferred_locale;
                    } else {
                        Settings.defaultLocale = 'en';
                    }

                    resolve(this._user);
                })
                .catch(e => {
                    if (e.response.status === 401) {
                        return resolve(null);
                    }

                    reject(e);
                });
        });
    }

    logout(): void {
        window.axios.post('/auth/logout')
            .then(() => {
                window.location = (window.global.domain + '/auth/login' as unknown as Location);
            })
    }

    set user(user: User | null) {
        this._user = user;
    }

    get user(): User | null {
        return this._user;
    }
}
